'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import type { GenericRequest } from '@eliadmin/core/src/api/api-types';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { uploadSecretFile } from '@eliadmin/core/src/api/general/general-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm, type UseFormReturn } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { useRef, useState, type ChangeEvent } from 'react';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import Link from 'next/link';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().optional(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    subject: z.string().optional(),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    attachments: z.array(z.object({ id: z.number() })).optional(),
    'g-recaptcha-response': z.string().default(''),
  });

type ContactFormInputValues = z.input<ReturnType<typeof contactSchema>>;
export type ContactFormValues = z.output<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const showAttachments = !!templateSettings?.contact?.contact_form_attachments?.enabled;
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const MAX_FILES = 15;

  const { formValidationMessages } = useFormValidationMessages(t);

  const schema = contactSchema(formValidationMessages);

  const form = useForm<ContactFormInputValues, any, ContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      subject: '',
      text: '',
      gdpr: false,
      attachments: [],
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const uploadContactFile = async (req: GenericRequest<{ formData: FormData }>) => {
    return uploadSecretFile(req);
  };

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handlePickFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFileError(null);

    if (selectedFiles.length === 0) {
      setFiles([]);
      return;
    }

    if (selectedFiles.length > MAX_FILES) {
      setFileError(`${t('text3457')} ${MAX_FILES}`);
      setFiles([]);
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setFileError(t('text3458'));
      setFiles([]);
      return;
    }

    setFiles(selectedFiles);
  };

  const handleSubmit = form.handleSubmit(async data => {
    try {
      if (showAttachments && files.length > 0) {
        if (files.length > MAX_FILES) {
          setFileError(`${t('text3457')} ${MAX_FILES}`);
          return;
        }

        const attachments: { id: number }[] = [];

        for (const file of files) {
          if (file.size > MAX_FILE_SIZE) {
            setFileError(t('text3458'));
            return;
          }

          const formData = new FormData();
          formData.append('file', file);

          try {
            const res = await uploadContactFile({
              formData,
              locale,
            });

            if (res.data?.id) {
              attachments.push({ id: res.data.id });
            }
          } catch {
            setFileError(t('text3459'));
            return;
          }
        }

        data.attachments = attachments;
      }

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id || '',
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <div
        className={cn(
          'flex w-full flex-col items-start justify-start gap-4 rounded-[24px] border border-[#E5E7F2] p-4 sm:gap-5 sm:p-6 md:p-8 lg:gap-6 lg:p-12',
          'ea-contact-form__container',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <Heading title={t('text2008')} tag="h3" visualTag="h4" />
          <Paragraph className="text-[#646B96]" size="lg">
            Vyplňte formulár a my sa vám čo najskôr ozveme.
          </Paragraph>
        </div>
        <form
          onSubmit={handleSubmit}
          className={cn('grid w-full grid-cols-12 gap-x-4 gap-y-4 md:gap-y-6', 'ea-contact-form')}
        >
          <ContactField
            form={form}
            name="name"
            className="col-span-12 w-full sm:col-span-6"
            label="Meno a priezvisko"
            placeholder={t('text1044')}
          />
          <ContactField
            form={form}
            name="email"
            type="email"
            className="col-span-12 w-full sm:col-span-6"
            label="E-mail"
            placeholder="vas@email.sk"
          />
          <ContactField
            form={form}
            name="phone"
            type="tel"
            className="col-span-12 w-full"
            label="Telefón (voliteľné)"
          />
          <ContactField
            form={form}
            name="subject"
            type="select"
            className="col-span-12 w-full"
            label="Téma správy"
            options={[
              { label: 'Rezervácia', value: 'Rezervácia' },
              { label: 'Konzultácia', value: 'Konzultácia' },
              { label: 'Iné', value: 'Iné' },
            ]}
            placeholder="Vyberte tému —  rezervácia, konzultácia, iné…"
          />
          <ContactField
            form={form}
            name="text"
            type="textarea"
            className="col-span-12 w-full"
            label={t('text2340')}
            placeholder="Napíšte nám čo potrebujete…"
          />

          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'col-span-12 flex items-start',
                  'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className={cn(
                      'h-6 w-6',
                      'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox',
                    )}
                  />
                </FormControl>
                <FormLabel className={cn('ea-contact-form__gdpr-checkbox-label')}>
                  <Paragraph size="base" className="font-normal">
                    Súhlasím so spracovaním osobných údajov v zmysle{' '}
                    <Link
                      href={'/gdpr'}
                      className="text-primary underline transition-all hover:text-primary/75"
                    >
                      GDPR
                    </Link>{' '}
                    {t('text3354')}
                  </Paragraph>
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('col-span-12', 'hidden')}>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="col-span-12 flex w-full items-center justify-start">
            <Button
              type="submit"
              className={cn('w-fit', 'ea-contact-form__submit')}
              isLoading={form.formState.isSubmitting}
            >
              <ButtonIcon alt={t('text185')} url="/images/icons/send-2.svg" />
              {t('text2343')}
            </Button>
          </div>
        </form>
      </div>
    </Form>
  );
}

// This project's small contact form does not need the generic Field's multi-select,
// country database or international phone widget. FormControl keeps label/error IDs.
function ContactField({
  form,
  name,
  type = 'text',
  label,
  placeholder,
  className,
  options,
}: {
  form: UseFormReturn<ContactFormInputValues, any, ContactFormValues>;
  name: 'name' | 'email' | 'phone' | 'subject' | 'text';
  type?: 'text' | 'email' | 'tel' | 'textarea' | 'select';
  label: string;
  placeholder?: string;
  className: string;
  options?: { label: string; value: string }[];
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col items-start gap-1', className)}>
          <FormLabel className="m-0 text-sm font-semibold text-secondary">{label}</FormLabel>
          <FormControl>
            {type === 'select' ? (
              <select
                {...field}
                value={field.value ?? ''}
                className="h-12 w-full rounded-[12px] border bg-transparent px-[18px] text-sm text-secondary sm:h-[52px] lg:text-base"
              >
                <option value="">{placeholder}</option>
                {options?.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : type === 'textarea' ? (
              <Textarea {...field} value={field.value ?? ''} placeholder={placeholder} />
            ) : (
              <Input
                {...field}
                value={field.value ?? ''}
                type={type}
                placeholder={placeholder}
                autoComplete={
                  name === 'phone' ? 'tel' : name === 'name' || name === 'email' ? name : undefined
                }
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
